import { Link } from 'react-router-dom';
import sgLogoWide from '../assets/logo/sg-logo-wide.png';
import sgLogoWhite from '../assets/logo/sg-logo-white.png';
import { APP_VERSION } from '../config/version';
import './LandingPage.css';

/**
 * LandingPage - Public landing page for Seraphina Grey Proofs
 *
 * Serves as the customer-friendly entry point with:
 * - Hero section explaining the proofs system
 * - How It Works guide for customers expecting proof links
 * - CTA to visit the main Seraphina Grey shop
 * - Footer with subtle admin login
 */
const LandingPage = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-content">
          <img
            src={sgLogoWide}
            alt="Seraphina Grey"
            className="hero-logo"
          />
          <h1 className="hero-title">Welcome to Your Proof Review</h1>
          <p className="hero-tagline">
            Where Imagination Runs Wild and Dreams Take Flight
          </p>
          <p className="hero-description">
            Review and approve your custom photos with ease. If you received a proof
            link from Seraphina Grey, simply click the link in your email to view
            and approve your personalized creations.
          </p>
          <a
            href="https://seraphinagrey.com"
            className="cta-button primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit Our Shop
          </a>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="landing-how-it-works">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3>Receive Your Link</h3>
              <p>
                Check your email for your unique proof review link from Seraphina Grey.
                Each link is personalized just for you.
              </p>
            </div>
            <div className="step">
              <div className="step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3>Review Your Photos</h3>
              <p>
                Browse through each photo one by one. Approve the ones you love
                or request changes with detailed feedback.
              </p>
            </div>
            <div className="step">
              <div className="step-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>Get Your Certificate</h3>
              <p>
                Once all photos are approved, download your branded proof certificate
                as a record of your approval.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="cta-content">
          <h2 className="cta-title">Explore Seraphina Grey</h2>
          <p className="cta-description">
            Discover our collection of whimsical, artistic creations where
            imagination meets craftsmanship. From custom drinkware to enchanted
            jewelry, find something uniquely you.
          </p>
          <a
            href="https://seraphinagrey.com"
            className="cta-button secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Shop Now
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <img
            src={sgLogoWhite}
            alt="Seraphina Grey"
            className="footer-logo"
          />

          <nav className="footer-links" aria-label="Footer navigation">
            <a
              href="https://seraphinagrey.com/policies/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            <a
              href="https://seraphinagrey.com/policies/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            <a
              href="https://seraphinagrey.com/pages/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </a>
          </nav>

          <div className="footer-admin">
            <Link to="/admin/login" className="admin-link">
              Admin Login
            </Link>
          </div>

          <p className="footer-copyright">
            &copy; {currentYear} Seraphina Grey. All rights reserved.
          </p>
          <p className="footer-version">v{APP_VERSION}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
